import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useSetRecoilState } from 'recoil';
import { Spinner, ErrorMessage } from '@gsa/afp-component-library';
import {
  GET_SOLICITATION_BY_ID_WITH_CONTRACTS,
  GET_MULTIPLE_OPTIONS,
} from '../../queries';
import { processOpenPeriod } from '../../utils/open-reopen-period-util';
import { isValidUPIID } from '../../utils/solicitation-upiid-utils';
import { hasContractAwardedForNewSolicitationVersion } from '../../utils/solicitation-table-utils';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';

const UpiidDetailsContext = createContext({});

const UpiidDetailsProvider = ({ children }) => {
  const { id } = useParams();
  const location = useLocation();
  const [solicitation, setSolicitation] = useState(null);
  const [bidType, setBidType] = useState('IN_FLEET');
  const [options, setOptions] = useState([]);
  const [aacCodes, setAacCodes] = useState([]);
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );

  useEffect(() => {
    // Clear all notifications when component mounts
    setSolicitationNotification((prev) =>
      prev.filter((notification) => notification.showOnNextPage),
    );
  }, []);

  const {
    loading: solicitationLoading,
    data: solicitationData,
    refetch: refetchSolicitation,
  } = useQuery(GET_SOLICITATION_BY_ID_WITH_CONTRACTS, {
    skip: !id,
    variables: { id: Number(id) },
    fetchPolicy: 'network-only',
  });

  // Process solicitation data whenever it changes (initial load or refetch)
  useEffect(() => {
    if (solicitationData?.getSolicitationById) {
      const current = solicitationData.getSolicitationById;
      const formattedSolicitation = {
        upiidAAC: current.solicitationNumber.substring(0, 6),
        upiidSQ: current.solicitationNumber.substring(6, 13),
        solicitationTitle: current.title,
        solicitationDescription: current.description,
        solicitationPosting: current.samUrl,
        contractYear: current.contractYear,
        purchaseType: current.purchaseTypeCode,
        solicitationType: current.solicitationType,
        solicitationStatus: current.status,
        status: current.status, // for stepper component
        solicitationNumber: current.solicitationNumber,
        solicitationID: current.id,
        id: current.id,
        bidType: current.bidType,
        programs: current.programs,
        contracts: current.contracts,
        openPeriod: processOpenPeriod([
          ...solicitationData.getSolicitationById.solicitationPeriods,
        ]),
      };
      setSolicitation(formattedSolicitation);
    }
  }, [solicitationData]);

  const { loading: optionsLoading } = useQuery(GET_MULTIPLE_OPTIONS, {
    variables: {
      options: [
        {
          model: 'StandardsCodeModel',
          label: 'title',
          value: 'code',
          filter: {
            operator: 'EQ',
            key: 'category',
            value: 'Purchase Type',
          },
          uniqueKey: 'purchaseType',
          includeValueInLabel: false,
        },
        {
          model: 'StandardsCodeModel',
          label: 'code',
          value: 'code',
          filter: {
            operator: 'EQ',
            key: 'category',
            value: 'Activity Address Code',
          },
          uniqueKey: 'aac',
          includeValueInLabel: false,
        },
      ],
    },
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setOptions(data.getMultipleOptions);
      // Extract aacCodes from options
      if (data.getMultipleOptions) {
        const aacOptions = data.getMultipleOptions.filter(
          (opt) => opt.uniqueKey === 'aac',
        );
        setAacCodes(aacOptions.map(({ value }) => value));
      }
    },
  });

  const contextValue = useMemo(
    () => ({
      solicitation,
      refetchSolicitation,
      options,
      setOptions,
      aacCodes,
      bidType,
      setBidType,
    }),
    [
      solicitation,
      refetchSolicitation,
      options,
      setOptions,
      aacCodes,
      bidType,
      setBidType,
    ],
  );

  if (solicitationLoading || optionsLoading) {
    return (
      <div className="margin-top-8">
        <Spinner />
      </div>
    );
  }

  if (id && !solicitation) {
    return <ErrorMessage>Solicitation not found</ErrorMessage>;
  }

  // Validate edit/reopen/midcycle actions - block if NONE are allowed
  if (
    location.pathname.includes('edit') &&
    (!isValidUPIID(solicitation.solicitationNumber, aacCodes) ||
      !['Draft', 'New', 'Open', 'Closed'].includes(solicitation.status))
  ) {
    return (
      <ErrorMessage className="margin-top-2 margin-bottom-8">
        Edit is not allowed for this solicitation
      </ErrorMessage>
    );
  }

  // Validate renew action is allowed
  if (
    location.pathname.includes('renew') &&
    (!isValidUPIID(solicitation.solicitationNumber, aacCodes) ||
      solicitation.status !== 'Closed' ||
      solicitation.solicitationType === 'Standalone' ||
      hasContractAwardedForNewSolicitationVersion(solicitation))
  ) {
    return (
      <ErrorMessage className="margin-top-2 margin-bottom-8">
        Renew is not allowed for this solicitation
      </ErrorMessage>
    );
  }

  return (
    <UpiidDetailsContext.Provider value={contextValue}>
      {children}
    </UpiidDetailsContext.Provider>
  );
};

UpiidDetailsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UpiidDetailsProvider;

export const useUpiidDetails = () => useContext(UpiidDetailsContext);
